import React from 'react'
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro'
import { StringCell } from 'components/Shared/MuiTable/Cell/StringCell'
import InfoTooltipCell from 'components/Shared/MuiTable/Cell/InfoTooltipCell'
import { useNewTranslation } from 'hooks'
import Text from 'particles/Text'
import styled from 'particles/themes/styled'
import { PayoutState, TaskState } from 'state/tasks'
import { CampaignState, FollowerState } from 'state/campaigns'
import { REQUIREMENT_TYPE_ENUM } from 'state/campaigns'
import { RequirementState } from 'state/campaigns'
import { PLATFORM_ENUM, TASK_TYPE_ENUM } from 'services/api/constants'
import { GetTaskPerformanceResponse } from 'services/api/campaignReport'
import { getEngagementTranslation } from 'components/utils/translationsMap'
import i18n from 'config/i18n'
import _ from 'lodash'
import { convertToThousandSeparator } from 'components/utils'
interface EngagementMetrics {
    like: number
    save: number
    share: number
    comment: number
}

interface StepPerformance {
    submission_count?: number | null
    posting_count?: number | null
    follower_count?: number | null
    engagement_sum?: number | null
    engagement_metrics?: EngagementMetrics
    view_count?: number | null
    reach_count?: number | null
    impression?: number | null
}

interface Step {
    uuid: string
    title: string
    performance: StepPerformance
}

interface Task {
    uuid: string
    title: string
    type: TASK_TYPE_ENUM
    steps: Step[]
}

interface Submetric {
    id: string
    metric: {
        value: string
    }
    actual: {
        value: string
    }
    minimum: {
        value: string
    }
}

export interface TaskPerformanceRowProps {
    id: string
    uuid: string
    step: {
        value: string
    }
    metric: {
        value: string
        hasSubmetrics?: boolean
        info?: string
    }
    actual: {
        value: string
    }
    minimum: {
        value: string
    }
    submetrics?: Submetric[]
}

export interface TaskPerformanceGroupProps {
    title: string
    steps: (TaskPerformanceRowProps | object | null)[]
}

export const getTaskPerformanceColumnsDef = (onExpandClick: () => void, expandRow: boolean): GridColDef[] => {
    const [t] = useNewTranslation()

    return [
        {
            field: 'step',
            headerName: t('submission.campaignReport.step.title'),
            width: 240,
            resizable: false,
            align: 'left',
            headerClassName: 'align-left-header',
            cellClassName: 'title-cell',
            sortable: false,
            filterable: false,
            renderCell: (params: GridRenderCellParams) => <InfoTooltipCell {...params} />,
        },
        {
            field: 'metric',
            headerName: t('submission.campaignReport.metric.title'),
            minWidth: 120,
            align: 'left',
            headerClassName: 'align-left-header',
            cellClassName: expandRow ? 'expanded' : '',
            renderCell: (props: GridRenderCellParams) => {
                if (props.id === 'submetric') {
                    return (
                        <CustomEngagementMetricsCell align="left">
                            {props.value.map((item: { value: string | number }, index: number) => (
                                <Text
                                    key={index}
                                    scale="bodyM500"
                                    color={item.value ? 'primary' : 'placeholder'}
                                    className="text"
                                >
                                    {item.value ? ' \u2022 ' + item.value : '-'}
                                </Text>
                            ))}
                        </CustomEngagementMetricsCell>
                    )
                }
                if (props.value.info) {
                    return <InfoTooltipCell {...props} />
                }
                return (
                    <StringCell
                        {...props}
                        hasIcon={props.id === 'engagement_metrics' && props.value.hasSubmetrics}
                        icon={expandRow ? 'caretUp' : 'caretDown'}
                        onClick={() => onExpandClick()}
                    />
                )
            },
            sortable: false,
            filterable: false,
            flex: 1,
        },
        {
            field: 'actual',
            headerName: t('submission.campaignReport.actual.header'),
            minWidth: 120,
            align: 'center',
            renderCell: (props: GridRenderCellParams) => {
                if (props.id === 'submetric') {
                    return (
                        <CustomEngagementMetricsCell align="center">
                            {props.value.map((item: { value: string | number }, index: number) => (
                                <Text
                                    key={index}
                                    scale="bodyM500"
                                    color={item.value ? 'primary' : 'placeholder'}
                                    className="text"
                                >
                                    {item.value ? item.value : '-'}
                                </Text>
                            ))}
                        </CustomEngagementMetricsCell>
                    )
                }
                return <StringCell {...props} />
            },
            sortable: false,
            filterable: false,
            flex: 1,
        },
        {
            field: 'minimum',
            headerName: t('submission.campaignReport.minimum.header'),
            headerClassName: 'minimum-header',
            minWidth: 120,
            align: 'center',
            sortable: false,
            filterable: false,
            renderCell: (props: GridRenderCellParams) => {
                if (props.id === 'submetric') {
                    return (
                        <CustomEngagementMetricsCell align="center">
                            {props.value.map((item: { value: string | number }, index: number) => (
                                <Text
                                    key={index}
                                    scale="bodyM500"
                                    color={item.value ? 'primary' : 'placeholder'}
                                    className="text"
                                >
                                    {item.value ? item.value : '-'}
                                </Text>
                            ))}
                        </CustomEngagementMetricsCell>
                    )
                }
                return <StringCell {...props} />
            },
            flex: 1,
        },
    ]
}

const transformSubmetrics = (baseStep: { submetrics: Submetric[] }) => {
    if (!baseStep.submetrics?.length) return null

    return {
        id: 'submetric',
        metric: baseStep.submetrics.map((sub: Submetric) => sub.metric),
        actual: baseStep.submetrics.map((sub: Submetric) => sub.actual),
        minimum: baseStep.submetrics.map((sub: Submetric) => sub.minimum),
        step: { value: '' },
        isSubmetric: true,
    }
}

const transformTasksToMetrics = (tasks: TaskState[]) => {
    return tasks.reduce(
        (acc, task) => {
            // Only include tasks that have a type and payouts
            if (task.type && task.payouts) {
                acc[task.type] = {
                    payouts: task.payouts,
                }
            }
            return acc
        },
        {} as Record<TASK_TYPE_ENUM, { payouts: PayoutState }>,
    )
}

// map task type to platform
const taskTypeToPlatform: Partial<Record<TASK_TYPE_ENUM, PLATFORM_ENUM>> = {
    [TASK_TYPE_ENUM.IG_POST]: PLATFORM_ENUM.IG,
    [TASK_TYPE_ENUM.IG_POST_V02]: PLATFORM_ENUM.IG,
    [TASK_TYPE_ENUM.IG_STORY]: PLATFORM_ENUM.IG,
    [TASK_TYPE_ENUM.IG_STORY_V02]: PLATFORM_ENUM.IG,
    [TASK_TYPE_ENUM.IG_REEL]: PLATFORM_ENUM.IG,
    [TASK_TYPE_ENUM.FB_POST]: PLATFORM_ENUM.FB,
    [TASK_TYPE_ENUM.FB_POST_V02]: PLATFORM_ENUM.FB,
    [TASK_TYPE_ENUM.TIKTOK_POST]: PLATFORM_ENUM.TIKTOK,
}

// Add other metrics for engagement step
const additionalMetrics = [
    ['view_count', i18n.t('submission.campaignReport.metric.totalViews')],
    ['reach_count', i18n.t('submission.campaignReport.metric.totalReach')],
    ['impression_count', i18n.t('submission.campaignReport.metric.totalImpression')],
]

const additionalMetricsTypeMap = {
    view_count: 'view',
    reach_count: 'reach',
    impression_count: 'impression',
}

const additionalMetricsTooltipMap = {
    reach_count: i18n.t('submission.table.reachTooltips'),
    impression_count: i18n.t('submission.table.impressionTooltips'),
}

export const getTaskPerformanceRowsDef = (
    data: GetTaskPerformanceResponse,
    campaign: CampaignState,
    tasks: TaskState[],
) => {
    const [t] = useNewTranslation()
    const followerRequirements = campaign.requirements.find(
        (requirement: RequirementState) => requirement.type == REQUIREMENT_TYPE_ENUM.FOLLOWER,
    )

    const getMinFollowers = (slotNumber: number | null, platform?: PLATFORM_ENUM) => {
        if (!platform) return
        const follower = followerRequirements?.properties.followers?.find(
            (follower: FollowerState) => follower.platform == platform,
        )
        if (!follower?.min || !slotNumber) return null
        return convertToThousandSeparator(follower.min * slotNumber)
    }

    const payoutMetrics = transformTasksToMetrics(tasks)

    return data.tasks.map((task: Task) => {
        const transformedSteps = task.steps.flatMap((step: Step) => {
            if (!step.performance) return []
            const performance = step.performance as StepPerformance

            const performanceMetrics = []

            // Handle submission count
            if ('submission_count' in performance) {
                performanceMetrics.push({
                    id: step.uuid,
                    uuid: step.uuid,
                    step: { value: step.title },
                    metric: { value: t('submission.campaignReport.metric.submission') },
                    actual: { value: convertToThousandSeparator(performance.submission_count) },
                    minimum: {
                        value: convertToThousandSeparator(campaign?.slot_number),
                    },
                })
            }

            // Handle posting count
            if ('posting_count' in performance) {
                performanceMetrics.push({
                    id: step.uuid,
                    uuid: step.uuid,
                    step: { value: step.title },
                    metric: { value: t('submission.campaignReport.metric.post') },
                    actual: { value: convertToThousandSeparator(performance.posting_count) },
                    minimum: {
                        value: convertToThousandSeparator(campaign?.slot_number),
                    },
                })
            }

            // Handle follower count
            if ('follower_count' in performance) {
                performanceMetrics.push({
                    id: step.uuid,
                    uuid: step.uuid,
                    step: { value: step.title },
                    metric: { value: t('submission.campaignReport.metric.followers') },
                    actual: { value: convertToThousandSeparator(performance.follower_count) },
                    minimum: {
                        value: task.type
                            ? getMinFollowers(campaign.slot_number, taskTypeToPlatform[task.type as TASK_TYPE_ENUM])
                            : null,
                    },
                })
            }

            // Handle engagement metrics
            if ('engagement_metrics' in performance && performance.engagement_metrics) {
                const baseStep = {
                    id: 'engagement_metrics',
                    uuid: step.uuid,
                    step: { value: '' },
                    metric: {
                        value: t('submission.campaignReport.metric.totalEngagement'),
                        hasSubmetrics: !_.isEmpty(performance.engagement_metrics),
                    },
                    actual: { value: convertToThousandSeparator(performance.engagement_sum) },
                    minimum: { value: null },
                    submetrics: Object.entries(performance.engagement_metrics).map(([key, value]) => ({
                        id: `${step.uuid}-${key}`,
                        metric: { value: getEngagementTranslation[key](t) },
                        actual: { value: convertToThousandSeparator(value) },
                        minimum: {
                            value: convertToThousandSeparator(
                                payoutMetrics[task.type]?.payouts[key as keyof PayoutState]?.min as number,
                            ),
                        },
                        step: { value: '' },
                    })),
                }
                performanceMetrics.push(baseStep)

                const submetrics = transformSubmetrics(baseStep)
                performanceMetrics.push(submetrics)

                additionalMetrics.forEach(([key, label]) => {
                    if (key in performance) {
                        const metricKey = additionalMetricsTypeMap[key as keyof typeof additionalMetricsTypeMap] ?? key
                        performanceMetrics.push({
                            id: key,
                            uuid: step.uuid,
                            step: { value: '' },
                            metric: {
                                value: label,
                                info: additionalMetricsTooltipMap?.[key as keyof typeof additionalMetricsTooltipMap],
                            },
                            actual: {
                                value: convertToThousandSeparator(performance[key as keyof StepPerformance] as number),
                            },
                            minimum: {
                                value: convertToThousandSeparator(
                                    payoutMetrics[task.type]?.payouts[metricKey as keyof PayoutState]?.min as number,
                                ),
                            },
                        })
                    }
                })
            }

            return performanceMetrics
        })

        return {
            title: task.title,
            steps: transformedSteps,
        }
    })
}

const CustomEngagementMetricsCell = styled.div<{ align?: 'center' | 'left' }>`
    display: flex;
    flex-direction: column;
    gap: 4;
    width: 100%;
    align-items: ${({ align }) => align || 'center'};

    .text {
        padding: 6px 12px;
    }
`
